/**
 * A small turning ring, for "this is working".
 *
 * Used by: some forty screens, wherever something is loading or saving.
 *
 * @module Spinner
 */

import React from 'react';

/** How big the ring is and what colour it turns in. */
interface SpinnerProps {
  /** Diameter of the spinner in pixels. */
  size?: number;
  /**
   * Colour of the turning edge: any CSS colour (`currentColor` for the words'
   * colour on a button, `hsl(var(--primary-foreground))` for a theme colour),
   * or a `border-` name, which is read as the CSS variable of the same name
   * (`border-white` is `var(--white)`, which nothing defines: the edge then
   * draws in the surrounding text's colour, as `currentColor` does).
   * Defaults to the theme's main colour.
   */
  color?: string;
}

/**
 * Spinner component for displaying a loading indicator.
 *
 * The default is the theme's main colour (`hsl(var(--primary))`), as a colour
 * the browser can read. It was once the bare name `emerald-500`, which is not
 * a CSS colour: the browser dropped it, the one coloured edge stayed
 * transparent, and every spinner left on the default drew nothing at all.
 *
 * @param {SpinnerProps} props - The properties of the spinner.
 * @returns {React.ReactElement} The spinner element.
 */
const Spinner: React.FC<SpinnerProps> = ({ size = 12, color = 'hsl(var(--primary))' }) => {
  // Convert color string to CSS variable or direct color value
  const borderColor = color.startsWith('border-') ? `var(--${color.substring(7)})` : color;

  return (
    <div
      className="animate-spin rounded-full"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: 'transparent transparent transparent',
        borderTopColor: borderColor,
      }}
    />
  );
};

export default Spinner;
